import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { Timer, Pencil, Lightbulb, Loader2, Play, Square, RefreshCw, BookOpenCheck } from "lucide-react";

const TABS = [
  { id: "timed", label: "Timed Writing", icon: Timer },
  { id: "vocab", label: "Vocabulary", icon: Lightbulb },
];

export default function WritingPractice() {
  const [tab, setTab] = useState("timed");

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 grid place-items-center"><Pencil size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Writing Practice</h1>
        </div>
        <p className="text-slate-600">Daily prompts, timed sprints, and vocabulary reps. Five focused minutes beats an hour of scrolling.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`practice-tab-${t.id}`} onClick={()=>setTab(t.id)}
              className={`rounded-full px-4 py-2 text-sm font-bold border-2 inline-flex items-center gap-2 ${tab===t.id ? 'bg-rose-500 border-rose-600 text-white':'bg-white border-slate-200 text-slate-700 hover:border-rose-300'}`}>
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "timed" && <TimedWriting />}
      {tab === "vocab" && <VocabPractice />}
    </div>
  );
}

function TimedWriting() {
  const [daily, setDaily] = useState(null);
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [text, setText] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => { api.get("/practice/daily-prompt").then(r=>setDaily(r.data)); }, []);

  const start = () => {
    setRunning(true); setSeconds(0); setText(""); setFeedback(null);
    intervalRef.current = setInterval(()=>setSeconds(s=>s+1), 1000);
  };
  const stop = async () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    if (!text.trim()) { toast.error("Write at least one sentence first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/practice/timed-feedback", {
        prompt: daily?.prompt || "",
        student_writing: text,
        seconds_spent: seconds,
      });
      setFeedback(data);
      celebrateBadges(data._meta);
    } catch { toast.error("Couldn't fetch feedback."); }
    finally { setLoading(false); }
  };
  const reset = () => { clearInterval(intervalRef.current); setRunning(false); setSeconds(0); setText(""); setFeedback(null); };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const target = (daily?.suggested_minutes || 15) * 60;
  const pct = Math.min(100, (seconds / target) * 100);

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  return (
    <div className="space-y-6">
      <div className="tactile-card p-6 sm:p-8">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Today's prompt</div>
          {daily && (
            <span className="text-xs rounded-full bg-amber-100 text-amber-800 px-3 py-1 font-bold uppercase tracking-wider" data-testid="daily-suggested">
              Suggested: {daily.suggested_minutes} min
            </span>
          )}
        </div>
        <p data-testid="daily-prompt" className="text-xl text-slate-900 leading-relaxed font-medium" style={{fontFamily:'Outfit'}}>
          {daily?.prompt || "Loading…"}
        </p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-rose-500 text-white grid place-items-center">
              <Timer size={20}/>
            </div>
            <div>
              <div data-testid="practice-timer" className="text-3xl font-bold leading-none" style={{fontFamily:'Outfit'}}>{fmt(seconds)}</div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mt-1" data-testid="word-count">{wordCount} words</div>
            </div>
          </div>
          <div className="flex gap-2">
            {!running && !feedback && (
              <button data-testid="practice-start" onClick={start}
                className="bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2"
                style={{boxShadow:'0 4px 0 0 rgb(190 18 60)'}}>
                <Play size={14}/> Start
              </button>
            )}
            {running && (
              <button data-testid="practice-stop" onClick={stop} disabled={loading}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={14}/> : <Square size={14}/>} Finish
              </button>
            )}
            {(feedback || (!running && seconds > 0)) && (
              <button data-testid="practice-reset" onClick={reset}
                className="bg-white border-2 border-slate-200 text-slate-900 font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 hover:border-slate-300">
                <RefreshCw size={14}/> New session
              </button>
            )}
          </div>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-rose-500 transition-all" style={{width:`${pct}%`}} />
        </div>
        <textarea data-testid="practice-textarea" value={text} onChange={(e)=>setText(e.target.value)}
          rows={10} disabled={!running}
          placeholder={running ? "Start writing…" : "Press Start to begin a focused session."}
          className="w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none resize-none font-mono disabled:opacity-60" />
      </div>

      {feedback && (
        <div data-testid="practice-feedback" className="grid md:grid-cols-2 gap-4 fade-up">
          <div className="tactile-card p-6 bg-emerald-50 border-emerald-100">
            <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-2">Strength</div>
            <p className="text-slate-900">{feedback.one_strength}</p>
          </div>
          <div className="tactile-card p-6 bg-amber-50 border-amber-100">
            <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-2">Growth area</div>
            <p className="text-slate-900">{feedback.one_growth}</p>
          </div>
          <div className="tactile-card p-6 bg-indigo-50 border-indigo-100 md:col-span-2">
            <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-2">Quick tip</div>
            <p className="text-slate-900 font-semibold">{feedback.quick_tip}</p>
            <div className="text-sm text-slate-500 mt-3">
              {feedback.word_count} words · pace: <strong>{feedback.pace_label}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VocabPractice() {
  const [word, setWord] = useState(null);
  const [sentence, setSentence] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = () => api.get("/vocab/word").then(r=>{ setWord(r.data); setSentence(""); setFeedback(null); });
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!sentence.trim()) { toast.error("Write a sentence first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/vocab/practice", { word: word.word, sentence });
      setFeedback(data);
      celebrateBadges(data._meta);
    } catch { toast.error("Couldn't grade your sentence."); }
    finally { setLoading(false); }
  };

  if (!word) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="tactile-card p-6 sm:p-8">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Word of the day</div>
          <span className="text-xs rounded-full bg-violet-100 text-violet-800 px-3 py-1 font-bold uppercase tracking-wider">{word.pos}</span>
        </div>
        <h2 data-testid="vocab-word" className="text-4xl font-bold mb-2" style={{fontFamily:'Outfit'}}>{word.word}</h2>
        <p className="text-slate-700 mb-3"><strong>Definition:</strong> {word.definition}</p>
        <p className="text-slate-600 italic">e.g. "{word.example}"</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div className="rounded-2xl bg-violet-50 border-2 border-violet-100 p-4 flex items-start gap-3">
          <BookOpenCheck size={18} className="text-violet-600 mt-0.5 flex-shrink-0"/>
          <div>
            <div className="text-xs uppercase font-bold tracking-widest text-violet-800 mb-1">Your challenge</div>
            <div className="text-slate-900">{word.challenge}</div>
          </div>
        </div>
        <textarea data-testid="vocab-sentence" value={sentence} onChange={(e)=>setSentence(e.target.value)}
          rows={3} placeholder={`Write a sentence using "${word.word}"…`}
          className="w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-4 py-3 outline-none resize-none" />
        <div className="flex gap-2 flex-wrap">
          <button data-testid="vocab-submit" onClick={submit} disabled={loading}
            className="bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
            style={{boxShadow:'0 4px 0 0 rgb(91 33 182)'}}>
            {loading ? <><Loader2 className="animate-spin" size={14}/> Checking…</> : "Get feedback"}
          </button>
          <button data-testid="vocab-refresh" onClick={refresh}
            className="bg-white border-2 border-slate-200 text-slate-900 font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 hover:border-slate-300">
            <RefreshCw size={14}/> New word
          </button>
        </div>
      </div>

      {feedback && (
        <div data-testid="vocab-feedback" className="tactile-card p-6 fade-up">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${feedback.used_word ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {feedback.used_word ? "Used the word ✓" : "Word missing"}
              </span>
              {typeof feedback.clarity_score === 'number' && (
                <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800">
                  Clarity {feedback.clarity_score}/4
                </span>
              )}
            </div>
          </div>
          <p className="text-slate-900 font-semibold mb-3">{feedback.feedback}</p>
          {feedback.better_example && (
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-slate-700 italic">
              {feedback.better_example}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
